/**
 * Product Customization Form Handler
 * 
 * Reemplaza el sistema de secuestro AJAX con un enfoque de inputs dinámicos
 * que permite que el formulario se envíe normalmente y sea detectado por
 * sistemas de carrito de terceros.
 * 
 * En lugar de interceptar el click y hacer AJAX, este sistema:
 * 1. Habilita/deshabilita inputs dinámicos según el estado de personalización
 * 2. Sincroniza valores entre inputs base y addon
 * 3. Deja que el formulario se envíe normalmente
 */

class ProductCustomizationFormHandler {
  constructor() {
    this.isCustomizationActive = false;
    this.isFreeCustomization = true;
    this.addonInputs = [];
    this.addonPropertyInputs = [];
    this.basePropertyInputs = {};
    this.quickBuyContainer = null;

    this._init();
  }

  _init() {
    // Buscar elementos del DOM
    this._findElements();

    // Configurar event listeners
    this._setupEventListeners();

    // Determinar si la personalización es gratuita
    this._checkCustomizationMode();

    // Inicializar el parche de equipo si está habilitado
    this._initializeTeamPatch();
  }

  _findElements() {
    // Buscar todos los inputs del addon (disabled por defecto)
    this.addonInputs = Array.from(document.querySelectorAll('.customization-addon-input'));
    this.addonPropertyInputs = Array.from(document.querySelectorAll('.customization-addon-property'));
    
    // Buscar inputs base de propiedades
    this.basePropertyInputs = {
      nombre: document.querySelector('#product_form_player_name'),
      dorsal: document.querySelector('#product_form_player_number'),
      opciones: document.querySelector('#product_form_customization_options'),
      modelo: document.querySelector('#product_form_modelo'),
      parche: document.querySelector('#product_form_team_patch')
    };
    
    // Buscar contenedor de Quick Buy
    this.quickBuyContainer = document.querySelector('.accelerated-checkout-block');
  }

  _setupEventListeners() {
    // Escuchar cambios en los inputs base para sincronizar con addon
    if (this.basePropertyInputs.nombre) {
      this.basePropertyInputs.nombre.addEventListener('input', () => this._syncAddonProperties());
    }
    if (this.basePropertyInputs.dorsal) {
      this.basePropertyInputs.dorsal.addEventListener('input', () => this._syncAddonProperties());
    }
    if (this.basePropertyInputs.modelo) {
      this.basePropertyInputs.modelo.addEventListener('input', () => this._syncAddonProperties());
    }
    if (this.basePropertyInputs.parche) {
      this.basePropertyInputs.parche.addEventListener('input', () => this._syncAddonProperties());
    }

    // Escuchar cambios en el tipo de personalización
    const customizationTypeSelect = document.querySelector('#customization_type');
    if (customizationTypeSelect) {
      customizationTypeSelect.addEventListener('change', () => {
        this._handleCustomizationTypeChange();
      });
    }

    // CRÍTICO: Crear el input parent_id desde el inicio
    this._createParentIdInput();

    // Escuchar cambios en la variante para actualizar parent_id y product info
    const variantIdInput = document.querySelector('input[name="id"]');
    if (variantIdInput) {
      // Actualizar parent_id y product info cuando cambia la variante
      const observer = new MutationObserver(() => {
        this._updateParentId();
        this._updateProductInfo();
      });
      observer.observe(variantIdInput, { attributes: true, attributeFilter: ['value'] });

      // También escuchar el evento change
      variantIdInput.addEventListener('change', () => {
        this._updateParentId();
        this._updateProductInfo();
      });
    }

    // Actualizar valores iniciales
    this._updateParentId();
    this._updateProductInfo();
  }

  _checkCustomizationMode() {
    const personalizadorBlock = document.querySelector('[data-block-type="personalizador_camisetas"]');
    this.isFreeCustomization = personalizadorBlock && personalizadorBlock.dataset.freeCustomization === 'true';
  }

  /**
   * Inicializa el parche de equipo con el valor por defecto si está habilitado
   */
  _initializeTeamPatch() {
    const enableTeamPatchElement = document.querySelector('[data-enable-team-patch]');
    const enableTeamPatch = enableTeamPatchElement ? enableTeamPatchElement.dataset.enableTeamPatch === 'true' : false;

    if (!enableTeamPatch) {
      return;
    }

    // Verificar si ya hay un valor establecido
    const teamPatchField = this.basePropertyInputs.parche;
    if (teamPatchField && !teamPatchField.value) {
      // Establecer el valor por defecto (Parche Masculino)
      teamPatchField.value = 'Parche Masculino';
    }
  }

  /**
   * Crea el input parent_id en el DOM desde el inicio
   * Esto es necesario para que esté disponible cuando se construye el FormData
   */
  _createParentIdInput() {
    // Buscar si ya existe
    let parentIdInput = document.querySelector('.customization-addon-input[name="items[1]parent_id"]');
    if (parentIdInput) {
      this.parentIdInput = parentIdInput;
      return;
    }

    // Crear el input
    parentIdInput = document.createElement('input');
    parentIdInput.type = 'hidden';
    parentIdInput.className = 'customization-addon-input';
    parentIdInput.name = 'items[1]parent_id';
    parentIdInput.value = ''; // Se actualizará dinámicamente
    parentIdInput.disabled = true; // Deshabilitado por defecto

    // Insertar junto con los otros inputs del addon
    const addonIdInput = document.querySelector('.customization-addon-input[name="items[1]id"]');
    if (addonIdInput && addonIdInput.parentNode) {
      addonIdInput.parentNode.insertBefore(parentIdInput, addonIdInput.nextSibling);
    }

    // Guardar referencia
    this.parentIdInput = parentIdInput;
  }

  /**
   * Actualiza el valor del parent_id con el variant ID actual
   * Se llama cada vez que cambia la variante seleccionada
   */
  _updateParentId() {
    if (!this.parentIdInput) return;

    const baseVariantIdInput = document.querySelector('input[name="id"]');
    if (baseVariantIdInput && baseVariantIdInput.value) {
      const baseVariantId = baseVariantIdInput.value;
      this.parentIdInput.value = baseVariantId;
    }
  }

  /**
   * Actualiza el campo "Producto" del addon con el nombre del producto + variante
   * Se llama cuando se habilita personalización o cuando cambia la variante
   */
  _updateProductInfo() {
    const addonProducto = document.querySelector('#addon_property_producto');
    if (!addonProducto) return;

    // Obtener título del producto
    const productTitleElement = document.querySelector('h1');
    const productTitle = productTitleElement ? productTitleElement.textContent.trim() : '';

    // Obtener título de la variante seleccionada
    const variantTitleElement = document.querySelector('variant-picker script[type="application/json"]');
    let variantTitle = '';
    if (variantTitleElement) {
      try {
        const variantData = JSON.parse(variantTitleElement.textContent);
        variantTitle = variantData.title || '';
      } catch (e) {
        // Silently fail
      }
    }

    // Actualizar el campo
    if (productTitle && variantTitle) {
      addonProducto.value = `${productTitle} / ${variantTitle}`;
    } else if (productTitle) {
      addonProducto.value = productTitle;
    }
  }

  /**
   * Activa o desactiva la personalización
   * @param {boolean} isActive - Si la personalización está activa
   */
  setCustomizationActive(isActive) {
    this.isCustomizationActive = isActive;

    if (isActive) {
      this._enableCustomization();
    } else {
      this._disableCustomization();
    }
  }

  _enableCustomization() {
    if (!this.isFreeCustomization) {
      // PERSONALIZACIÓN DE PAGO
      // Deshabilitar inputs base (las propiedades van solo en el addon)
      this._disableBasePropertyInputs();

      // Habilitar inputs del addon
      this._updateParentId();
      this._updateProductInfo();
      this._enableAddonInputs();
      this._syncAddonProperties();

      // Habilitar el parche en el addon si está configurado
      this._enableTeamPatchForAddon();
    } else {
      // PERSONALIZACIÓN GRATIS
      // Habilitar inputs base (las propiedades van en el producto base)
      this._enableBasePropertyInputs();

      // Deshabilitar inputs del addon (no se usa addon en modo gratis)
      this._disableAddonInputs();

      // Habilitar el parche en el producto base si está configurado
      this._enableTeamPatchForBase();
    }

    // Ocultar Quick Buy
    this._hideQuickBuy();
  }

  _disableCustomization() {
    // Deshabilitar inputs del addon
    this._disableAddonInputs();

    // Deshabilitar inputs base
    this._disableBasePropertyInputs();

    // Limpiar valores base
    Object.values(this.basePropertyInputs).forEach(input => {
      if (input) input.value = '';
    });

    // Restaurar el valor por defecto del parche si está habilitado
    const enableTeamPatchElement = document.querySelector('[data-enable-team-patch]');
    const enableTeamPatch = enableTeamPatchElement ? enableTeamPatchElement.dataset.enableTeamPatch === 'true' : false;

    if (enableTeamPatch && this.basePropertyInputs.parche) {
      // Cuando no hay personalización pero el selector de parche está habilitado,
      // mantener el valor por defecto y habilitar el campo
      this.basePropertyInputs.parche.value = 'Parche Masculino';
      this.basePropertyInputs.parche.disabled = false;
      this.basePropertyInputs.parche.removeAttribute('disabled');
    }

    // Mostrar Quick Buy
    this._showQuickBuy();
  }

  _enableAddonInputs() {
    this.addonInputs.forEach(input => {
      input.disabled = false;
      input.removeAttribute('disabled');
    });

    // También habilitar el parent_id
    if (this.parentIdInput) {
      this.parentIdInput.disabled = false;
      this.parentIdInput.removeAttribute('disabled');
    }
  }

  _disableAddonInputs() {
    this.addonInputs.forEach(input => {
      input.disabled = true;
      input.setAttribute('disabled', 'disabled');
    });

    // También deshabilitar el parent_id
    if (this.parentIdInput) {
      this.parentIdInput.disabled = true;
      this.parentIdInput.setAttribute('disabled', 'disabled');
    }
  }

  _enableBasePropertyInputs() {
    Object.entries(this.basePropertyInputs).forEach(([key, input]) => {
      if (input) {
        input.disabled = false;
        input.removeAttribute('disabled');
      }
    });
  }

  _disableBasePropertyInputs() {
    Object.entries(this.basePropertyInputs).forEach(([key, input]) => {
      if (input) {
        input.disabled = true;
        input.setAttribute('disabled', 'disabled');
      }
    });
  }

  /**
   * Habilita el campo de parche de equipo en el producto base (personalización gratis)
   */
  _enableTeamPatchForBase() {
    const enableTeamPatchElement = document.querySelector('[data-enable-team-patch]');
    const enableTeamPatch = enableTeamPatchElement ? enableTeamPatchElement.dataset.enableTeamPatch === 'true' : false;

    if (!enableTeamPatch) {
      return;
    }

    const teamPatchField = this.basePropertyInputs.parche;
    if (teamPatchField) {
      // Habilitar el campo
      teamPatchField.disabled = false;
      teamPatchField.removeAttribute('disabled');

      // Asegurar que tenga el valor por defecto si está vacío
      if (!teamPatchField.value) {
        teamPatchField.value = 'Parche Masculino';
      }
    }
  }

  /**
   * Habilita el campo de parche de equipo en el addon (personalización de pago)
   */
  _enableTeamPatchForAddon() {
    const enableTeamPatchElement = document.querySelector('[data-enable-team-patch]');
    const enableTeamPatch = enableTeamPatchElement ? enableTeamPatchElement.dataset.enableTeamPatch === 'true' : false;

    if (!enableTeamPatch) {
      return;
    }

    const addonParche = document.querySelector('#addon_property_parche');
    const teamPatchField = this.basePropertyInputs.parche;

    if (addonParche && teamPatchField) {
      // Asegurar que el campo base tenga el valor por defecto
      if (!teamPatchField.value) {
        teamPatchField.value = 'Parche Masculino';
      }

      // Sincronizar el valor al addon
      addonParche.value = teamPatchField.value;
    }
  }

  /**
   * Sincroniza los valores de los inputs base con los del addon
   */
  _syncAddonProperties() {
    // Solo sincronizar si la personalización está activa y es de pago
    if (!this.isCustomizationActive || this.isFreeCustomization) {
      return;
    }

    // Sincronizar nombre
    const addonNombre = document.querySelector('#addon_property_nombre');
    if (addonNombre && this.basePropertyInputs.nombre) {
      addonNombre.value = this.basePropertyInputs.nombre.value;
    }

    // Sincronizar dorsal
    const addonDorsal = document.querySelector('#addon_property_dorsal');
    if (addonDorsal && this.basePropertyInputs.dorsal) {
      addonDorsal.value = this.basePropertyInputs.dorsal.value;
    }

    // Sincronizar modelo
    const addonModelo = document.querySelector('#addon_property_modelo');
    if (addonModelo && this.basePropertyInputs.modelo) {
      addonModelo.value = this.basePropertyInputs.modelo.value;
    }

    // Sincronizar parche
    const addonParche = document.querySelector('#addon_property_parche');
    if (addonParche && this.basePropertyInputs.parche) {
      addonParche.value = this.basePropertyInputs.parche.value;
    }
  }

  /**
   * Handler que se ejecuta antes del submit
   * Asegura que todo esté actualizado antes de enviar el formulario
   */
  _handleBeforeSubmit(event) {
    // Solo actuar si hay personalización activa y es de pago
    if (!this.isCustomizationActive || this.isFreeCustomization) {
      return;
    }

    // Asegurar que todo esté actualizado (por si acaso)
    this._updateParentId();
    this._updateProductInfo();
    this._syncAddonProperties();
  }

  _handleCustomizationTypeChange() {
    const customizationType = document.querySelector('#customization_type')?.value;
    
    // Determinar si hay personalización activa
    const hasCustomization = customizationType === 'player' || customizationType === 'user';
    
    this.setCustomizationActive(hasCustomization);
  }

  _hideQuickBuy() {
    if (this.quickBuyContainer) {
      this.quickBuyContainer.style.display = 'none';
    }
  }

  _showQuickBuy() {
    if (this.quickBuyContainer) {
      // Verificar si hay un selector de parche habilitado
      const teamPatchWrapper = document.getElementById('team-patch-selection-wrapper');
      const isPatchSelectorVisible = teamPatchWrapper && teamPatchWrapper.style.display !== 'none';
      
      const enableTeamPatchElement = document.querySelector('[data-enable-team-patch]');
      const enableTeamPatch = enableTeamPatchElement ? enableTeamPatchElement.dataset.enableTeamPatch === 'true' : false;
      
      const teamPatchField = document.querySelector('input[name="properties[Parche]"]');
      const hasActiveTeamPatch = teamPatchField && teamPatchField.value && teamPatchField.value !== '';
      
      // Ocultar si: el selector está habilitado O visible O hay una selección activa
      if (enableTeamPatch || isPatchSelectorVisible || hasActiveTeamPatch) {
        this.quickBuyContainer.style.display = 'none';
      } else {
        this.quickBuyContainer.style.display = '';
      }
    }
  }

  /**
   * Método público para uso externo
   * Verifica si hay personalización activa
   */
  hasActiveCustomization() {
    return this.isCustomizationActive;
  }

  /**
   * Método público para limpiar personalización
   */
  clearCustomization() {
    this.setCustomizationActive(false);
  }
}

// Inicializar cuando el DOM esté listo
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.productCustomizationFormHandler = new ProductCustomizationFormHandler();
  });
} else {
  window.productCustomizationFormHandler = new ProductCustomizationFormHandler();
}

// Exportar para uso en módulos
if (typeof module !== 'undefined' && module.exports) {
  module.exports = ProductCustomizationFormHandler;
}

